import {createDefinitionForm} from '@github-ui/custom-properties-core/definition-form'
import type {DefinitionFormSchema} from '@github-ui/custom-properties-core/definition-form-types'
import {repoCustomPropertyValueValidator} from '@github-ui/custom-properties-core/helpers/validate'
import {usePropertyDefinitionForm} from '@github-ui/custom-properties-core/hooks/use-property-definition-form'
import type {
  CustomPropertyDetailsPagePayload,
  PropertyDefinition,
  PropertyValue,
  ValuesEditableBy,
} from '@github-ui/custom-properties-types'
import {isEmptyPropertyValue} from '@github-ui/custom-properties-types/helpers'
import {customPropertyDetailsPath} from '@github-ui/paths'
import {useQuery} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

export interface RepoPropertyDefinitionWithValueSchema extends DefinitionFormSchema {
  requireExplicitValues: boolean
  valuesEditableBy: ValuesEditableBy
  appliedValue: PropertyValue
}

interface Props {
  sourceName: string
}

const formSchema = createDefinitionForm<RepoPropertyDefinitionWithValueSchema>([
  {
    field: 'appliedValue',
    validate: async (data, _, ctx) => {
      if (isEmptyPropertyValue(data.appliedValue)) {
        return {fieldErrors: {appliedValue: ['Value is required']}}
      }

      const definition: Omit<PropertyDefinition, 'source'> = {
        propertyName: data.propertyName,
        valueType: data.valueType,
        required: data.required,
        defaultValue: data.defaultValue || null,
        description: data.description || null,
        allowedValues: data.allowedValues,
        regex: data.regexPattern || null,
        valuesEditableBy: data.valuesEditableBy,
        requireExplicitValues: data.requireExplicitValues,
      }
      const validator = repoCustomPropertyValueValidator([definition], ctx.propertiesLimits)
      const error = await validator(data.propertyName, data.appliedValue)

      if (error) {
        return {fieldErrors: {appliedValue: [error]}}
      }
    },
  },
])

export function useDefinitionFormWithValue({sourceName}: Props) {
  const {data, error: propertiesFetchError} = useSettingsRoutePayload({sourceName})

  const formProps = usePropertyDefinitionForm<RepoPropertyDefinitionWithValueSchema>({
    form: formSchema,
    formMode: 'create',
    propertiesLimits: data?.propertiesLimits,
    existingPropertyNames: data?.propertyNames || [],
    initialValues: {
      propertyName: '',
      description: '',
      valueType: 'string',
      allowedValues: [],
      defaultValue: '',
      regexEnabled: false,
      regexPattern: '',
      required: false,
      newAllowedValue: '',
      valuesEditableBy: 'org_actors',
      requireExplicitValues: false,
      appliedValue: '',
    },
  })

  return {formProps, propertiesFetchError}
}

function useSettingsRoutePayload({sourceName}: {sourceName: string}) {
  const path = customPropertyDetailsPath({pathPrefix: 'organizations', sourceName})

  return useQuery({
    queryKey: ['sidebar-definition-form', path],
    queryFn: async () => {
      const response = await verifiedFetchJSON(path)
      if (!response.ok) {
        throw new Error(`Failed to fetch properties: ${response.status}`)
      }
      const data = (await response.json()) as {payload: CustomPropertyDetailsPagePayload}
      return data.payload
    },
  })
}
